import { useCallback, useEffect, useRef } from 'react';

import {
  getCounterOffer,
  getEventWithLocation,
  pharmacistActions,
  pharmacistEvent,
  pharmacyActions,
  resetStatus,
  successOrSelector,
  successSelector,
} from '@pharmaplan/common';

import { useAppSelector } from '../useAppSelector';
import { useAppDispatch } from '../useAppDispatch';
import { selectFooterPaginationCurrent } from '../../selectors/drawerSelector';
import { IServiceWorkshiftDetails } from '../../components/SelfService/ServiceWorkshiftDetails/ServiceWorkshiftDetails';
import useServiceWorkshiftDetails from './useServiceWorkshiftDetails';
import { resetDrawer } from '../../actions/drawerActions';
import useUpdateContract from '../LongTermContract/useUpdateContract';
import { resetDialog } from '../../reducers/dialogReducer';

const useServiceWorkshiftDetailsEvent = ({
  eventIds,
  eventType,
  isAutoSyncIDMap,
}: IServiceWorkshiftDetails) => {
  const dispatch = useAppDispatch();

  const event = useAppSelector(pharmacistEvent);
  const { longTermShiftContract } = event ?? {};
  const { longTermShiftContractId, startDate, endDate } = longTermShiftContract ?? {};

  const current = useAppSelector(selectFooterPaginationCurrent);
  const id = useRef(eventIds[current - 1]);
  const isAutoSyncRecord = useRef(isAutoSyncIDMap?.[id.current]);

  const { setAttributes } = useUpdateContract();

  const getEventSuccess = useAppSelector((state) =>
    successSelector([pharmacistActions.getPharmacistEvent], state));

  const successes = useAppSelector((state) =>
    successOrSelector(
      [
        pharmacistActions.cancelBooking,
        pharmacistActions.cancelRequestedBooking,
        pharmacistActions.cancelContractRequest,
        pharmacistActions.requestBooking,
        pharmacyActions.ignoreRequest,
        pharmacyActions.approveRequest,
        pharmacistActions.rejectUpdatedTimings,
        pharmacistActions.acceptUpdatedTimings,
        pharmacistActions.selfCancelBooking,
        pharmacistActions.cancelMultipleBookings,
        pharmacistActions.requestContract,
      ],
      state,
    ));

  const {
    serviceConfig,
    goToCounterOffer,
    buttonConfig,
    isBooked,
    isWorkshift,
    isSelfWorkshift,
    isCounterOffer,
  } = useServiceWorkshiftDetails(
    id.current,
    eventType,
    isAutoSyncRecord.current,
  );

  const getWorkshiftDetails = useCallback(
    (latitude: number | null = null, longitude: number | null = null) => {
      dispatch(
        getEventWithLocation({
          latitude,
          longitude,
          id: id.current,
          type: eventType,
          isAutoSyncRecord: isAutoSyncRecord.current,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eventType],
  );

  useEffect(() => {
    id.current = eventIds[current - 1];
    isAutoSyncRecord.current = isAutoSyncIDMap?.[id.current];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  useEffect(() => {
    if (getEventSuccess && isCounterOffer) {
      dispatch(getCounterOffer(id.current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getEventSuccess, isCounterOffer]);

  useEffect(() => {
    if (successes) {
      dispatch(resetDrawer());
      dispatch(resetStatus());
      dispatch(resetDialog());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successes]);

  useEffect(
    () =>
      () => {
        dispatch(resetStatus([pharmacistActions.getPharmacistEvent]));
      // eslint-disable-next-line react-hooks/exhaustive-deps
      },
    [],
  );

  useEffect(() => {
    getWorkshiftDetails();
  }, [getWorkshiftDetails, current]);

  useEffect(() => {
    if (longTermShiftContractId) {
      setAttributes(startDate, endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  return {
    serviceConfig,
    goToCounterOffer,
    buttonConfig,
    isBooked,
    isWorkshift,
    isSelfWorkshift,
    isCounterOffer,
    isAutoSyncRecord: isAutoSyncRecord.current,
  };
};

export default useServiceWorkshiftDetailsEvent;
